import React, { useCallback, useEffect, useRef, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Logo } from "./Nav";
import { MARKETS, TIMEFRAMES, hasAccess } from "../mock";
import { api, errMsg } from "../lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { ArrowLeft, TrendingUp, TrendingDown, Minus, Loader2 } from "lucide-react";

const POLL_MS = 4000;

const LiveIndicator = () => {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [sym, setSym] = useState("BTC");
  const [tf, setTf] = useState("1m");
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [history, setHistory] = useState([]);
  const lastSig = useRef(null);
  const cv = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get("/market/candles", { params: { symbol: sym, tf } });
      if (d.symbol !== sym || d.tf !== tf) return;
      setData(d); setErr("");
      const key = `${d.signal}-${d.candles[d.candles.length - 1].t}`;
      if (d.signal !== "WAIT" && lastSig.current !== key) {
        lastSig.current = key;
        setHistory((h) => [{ t: new Date().toLocaleTimeString(), signal: d.signal, price: d.price, score: d.score, dec: d.dec }, ...h].slice(0, 8));
      }
    } catch (e) { setErr(errMsg(e, "Market data unavailable.")); }
  }, [sym, tf]);

  useEffect(() => {
    setData(null); setHistory([]); lastSig.current = null;
    if (!user || !hasAccess(user)) return;
    load();
    const id = setInterval(load, POLL_MS);
    return () => clearInterval(id);
  }, [load, user]);

  useEffect(() => {
    const c = cv.current; if (!c || !data) return;
    const series = data.candles;
    const ctx = c.getContext("2d");
    const w = (c.width = c.clientWidth * 2), h = (c.height = c.clientHeight * 2);
    ctx.clearRect(0, 0, w, h);
    const padR = 150;
    let min = Infinity, max = -Infinity;
    series.forEach((k) => { min = Math.min(min, k.l); max = Math.max(max, k.h); });
    if (data.signal !== "WAIT") { min = Math.min(min, data.stop, data.target); max = Math.max(max, data.stop, data.target); }
    const y = (v) => h - 24 - ((v - min) / (max - min || 1)) * (h - 48);
    const cw = (w - padR) / series.length;
    ctx.strokeStyle = "rgba(255,176,32,0.06)"; ctx.lineWidth = 1;
    ctx.font = "20px JetBrains Mono"; ctx.fillStyle = "rgba(165,155,139,0.8)";
    for (let i = 0; i <= 5; i++) {
      const v = min + ((max - min) * i) / 5; const yy = y(v);
      ctx.beginPath(); ctx.moveTo(0, yy); ctx.lineTo(w - padR, yy); ctx.stroke();
      ctx.fillText(v.toFixed(data.dec), w - padR + 12, yy + 6);
    }
    series.forEach((k, i) => {
      const up = k.c >= k.o; const col = up ? "#ffb020" : "#ff4560";
      const x = i * cw + cw / 2;
      ctx.strokeStyle = col; ctx.lineWidth = 2;
      ctx.beginPath(); ctx.moveTo(x, y(k.h)); ctx.lineTo(x, y(k.l)); ctx.stroke();
      ctx.fillStyle = up ? "rgba(255,176,32,0.8)" : "rgba(255,69,96,0.8)";
      ctx.fillRect(x - cw * 0.32, y(Math.max(k.o, k.c)), cw * 0.64, Math.max(2, Math.abs(y(k.o) - y(k.c))));
    });
    const line = (v, color, label) => {
      ctx.setLineDash([8, 8]); ctx.strokeStyle = color; ctx.lineWidth = 2;
      ctx.beginPath(); ctx.moveTo(0, y(v)); ctx.lineTo(w - padR, y(v)); ctx.stroke(); ctx.setLineDash([]);
      ctx.fillStyle = color; ctx.fillRect(w - padR + 4, y(v) - 16, padR - 8, 32);
      ctx.fillStyle = "#1a0f00"; ctx.fillText(label, w - padR + 12, y(v) + 7);
    };
    ctx.setLineDash([4, 6]); ctx.strokeStyle = "rgba(120,170,255,0.45)";
    ctx.beginPath(); ctx.moveTo(0, y(data.vwap)); ctx.lineTo(w - padR, y(data.vwap)); ctx.stroke(); ctx.setLineDash([]);
    if (data.signal !== "WAIT") { line(data.stop, "#ff4560", "SL"); line(data.target, "#5ee6a0", "TP"); }
    line(data.price, "#ffe082", data.price.toFixed(data.dec));
  }, [data]);

  if (loading) return <div className="live" />;
  if (!user) return <Navigate to="/" replace />;
  if (!hasAccess(user)) return <Navigate to="/dashboard" replace />;

  const signal = data?.signal || "WAIT";
  const SigIcon = signal === "BUY" ? TrendingUp : signal === "SELL" ? TrendingDown : Minus;
  const f = (v) => (data ? Number(v).toLocaleString(undefined, { minimumFractionDigits: data.dec, maximumFractionDigits: data.dec }) : "—");

  return (
    <div className="live" data-testid="live-indicator">
      <div className="dash-nav">
        <Logo />
        <div className="dash-nav-r">
          <span className={`live-dot ${data && !data.market_open ? "closed" : ""}`}><i /> {data && !data.market_open ? "MARKET CLOSED" : "LIVE"}</span>
          <button className="nav-btn nb-login" onClick={() => nav("/dashboard")} data-testid="back-dash"><ArrowLeft size={12} /> Dashboard</button>
        </div>
      </div>
      <div className="live-wrap">
        <div className="live-bar">
          <Select value={sym} onValueChange={setSym}>
            <SelectTrigger className="live-select" data-testid="market-select"><SelectValue /></SelectTrigger>
            <SelectContent className="live-select-pop">
              {MARKETS.map((m) => <SelectItem key={m.sym} value={m.sym}>{m.sym} — {m.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="tf-group">
            {TIMEFRAMES.map((t) => (
              <button key={t} className={`tf ${tf === t ? "on" : ""}`} onClick={() => setTf(t)} data-testid={`tf-${t}`}>{t}</button>
            ))}
          </div>
          <div className="live-price" data-testid="live-price">
            {data ? f(data.price) : <Loader2 size={20} className="spin" />}
            {data && <span className={data.change_pct >= 0 ? "up" : "dn"}>{data.change_pct >= 0 ? "+" : ""}{data.change_pct.toFixed(2)}%</span>}
          </div>
        </div>
        {err && <div className="live-err" data-testid="live-error">{err} Retrying…</div>}

        <div className="live-grid">
          <div className="live-card chart-card">
            {!data && !err && <div className="chart-loading"><Loader2 size={22} className="spin" /> Loading real market data…</div>}
            <canvas ref={cv} className="live-chart" />
          </div>
          <div className="live-side">
            <div className={`live-card sig sig-${signal.toLowerCase()}`} data-testid="signal-card">
              <div className="lc-eye">Signal · {sym} · {tf}</div>
              <div className="sig-main"><SigIcon size={26} /> {signal}</div>
              <div className="sig-score">AI Score <b>{data ? data.score : "—"}/7</b> · Bias <b>{data?.bias || "—"}</b> · RSI <b>{data?.rsi ?? "—"}</b></div>
              {data && signal !== "WAIT" ? (
                <div className="sig-levels">
                  <div><span>Entry</span><b>{f(data.entry)}</b></div>
                  <div><span>Stop</span><b className="dn">{f(data.stop)}</b></div>
                  <div><span>Target</span><b className="up">{f(data.target)}</b></div>
                </div>
              ) : <div className="sig-wait">Waiting for 5+ factors to align…</div>}
            </div>
            <div className="live-card">
              <div className="lc-eye">Scoring Factors · {data?.bias || "—"} side</div>
              {(data?.factors || []).map((x) => (
                <div className="factor" key={x.name}><span>{x.name}</span><i className={x.on ? "on" : ""} /></div>
              ))}
            </div>
          </div>
        </div>

        <div className="live-card">
          <div className="lc-eye">Signals This Session</div>
          {history.length === 0 ? <div className="sig-wait">No signals yet this session.</div> : history.map((s, i) => (
            <div className="hist" key={i}><span>{s.t}</span><b className={s.signal === "BUY" ? "up" : "dn"}>{s.signal}</b><span>{s.price.toFixed(s.dec)}</span><span>{s.score}/7</span></div>
          ))}
        </div>
        <p className="live-disc">Real market data via {data?.source || "Yahoo Finance"} (may be delayed). Signals are generated by a rules-based 7-factor model. Educational purposes only — not financial advice.</p>
      </div>
    </div>
  );
};

export default LiveIndicator;
